import { useMemo, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import ProductCard from "./ProductCard";
import { Reveal } from "./Reveal";

const CATEGORIES = [
  { key: "mini-perfumes", label: "Mini Perfumes" },
  { key: "carteras", label: "Carteras" },
  { key: "accesorios", label: "Accesorios" },
  { key: "all", label: "Todo" },
];

export const SCENTS = [
  { key: "floral", label: "Floral" },
  { key: "dulce", label: "Dulce" },
  { key: "fresco", label: "Fresco" },
  { key: "amaderado", label: "Amaderado" },
];

const GENDERS = [
  { key: "all", label: "Todos" },
  { key: "mujer", label: "Damas" },
  { key: "hombre", label: "Caballeros" },
  { key: "unisex", label: "Unisex" },
];

export default function Catalog({ products, currency, onOpen }) {
  const [active, setActive] = useState("mini-perfumes");
  const [scent, setScent] = useState(null);
  const [gender, setGender] = useState("all");

  const filtered = useMemo(() => {
    let base = products.filter((p) => !p.is_upcoming && !p.is_giftset);
    if (active !== "all") base = base.filter((p) => p.category === active);
    if (gender !== "all") base = base.filter((p) => (p.gender || "unisex") === gender);
    if (scent) base = base.filter((p) => (p.scents || []).includes(scent));
    return base;
  }, [products, active, scent, gender]);

  const showScents = active === "mini-perfumes" || active === "all";

  return (
    <section
      id="catalogo"
      data-testid="catalog-section"
      className="max-w-[1400px] mx-auto px-5 md:px-10 py-24 md:py-32"
    >
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8">
        <Reveal>
          <span className="text-[11px] uppercase tracking-[0.3em] text-rose">
            El catálogo
          </span>
          <h2 className="mt-4 font-serif font-light text-4xl md:text-6xl leading-none">
            Nuestra <span className="italic text-rose">colección</span>
          </h2>
        </Reveal>

        <div className="flex flex-wrap gap-2">
          {CATEGORIES.map((c) => (
            <button
              key={c.key}
              data-testid={`catalog-filter-${c.key}`}
              onClick={() => { setActive(c.key); setScent(null); }}
              className={`px-5 py-2.5 rounded-full text-[11px] uppercase tracking-[0.18em] border transition-all duration-300 ${
                active === c.key
                  ? "bg-espresso text-cream border-espresso"
                  : "border-line text-taupe hover:border-espresso hover:text-espresso"
              }`}
            >
              {c.label}
            </button>
          ))}
        </div>
      </div>

      {/* Género: Damas / Caballeros / Unisex */}
      <div className="mt-10 flex flex-wrap items-center gap-2.5" data-testid="gender-filter">
        {GENDERS.map((g) => (
          <button
            key={g.key}
            data-testid={`gender-filter-${g.key}`}
            onClick={() => setGender(g.key)}
            className={`px-6 py-3 rounded-full text-[12px] uppercase tracking-[0.2em] border transition-all duration-300 ${
              gender === g.key
                ? "bg-rose text-cream border-rose shadow-sm"
                : "border-line text-taupe hover:border-rose hover:text-rose"
            }`}
          >
            {g.label}
          </button>
        ))}
      </div>

      {showScents && (
        <div className="mt-8 flex flex-wrap items-center gap-2" data-testid="scent-filter">
          <span className="text-[11px] uppercase tracking-[0.2em] text-taupe mr-2">
            Notas olfativas
          </span>
          <button
            data-testid="scent-filter-all"
            onClick={() => setScent(null)}
            className={`px-4 py-1.5 rounded-full text-[11px] tracking-[0.1em] border transition-colors ${
              scent === null ? "bg-rose text-cream border-rose" : "border-line text-taupe hover:border-rose"
            }`}
          >
            Todas
          </button>
          {SCENTS.map((s) => (
            <button
              key={s.key}
              data-testid={`scent-filter-${s.key}`}
              onClick={() => setScent(scent === s.key ? null : s.key)}
              className={`px-4 py-1.5 rounded-full text-[11px] tracking-[0.1em] border transition-colors ${
                scent === s.key ? "bg-rose text-cream border-rose" : "border-line text-taupe hover:border-rose"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      <AnimatePresence mode="wait">
        <motion.div
          key={`${active}-${scent}-${gender}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="mt-14 grid grid-cols-2 lg:grid-cols-3 gap-x-5 gap-y-12 md:gap-x-8 md:gap-y-16"
        >
          {filtered.map((p, i) => (
            <div key={p.id} className={i % 5 === 0 ? "lg:row-span-2" : ""}>
              <ProductCard product={p} currency={currency} tall={i % 5 === 0} onOpen={onOpen} />
            </div>
          ))}
        </motion.div>
      </AnimatePresence>

      {filtered.length === 0 && (
        <p className="text-center text-taupe mt-16 font-serif italic text-xl">
          Pronto añadiremos piezas a esta categoría.
        </p>
      )}
    </section>
  );
}
